import type { TableCell } from '../model/form.types';

export interface SelectedCell {
  row: number;
  col: number;
}

export function isSelectableCell(cells: TableCell[][], row: number, col: number): boolean {
  return !cells[row]?.[col]?.isMerged;
}

function countSelectableCellsInRow(
  cells: TableCell[][],
  rowIndex: number,
  columns: number
): number {
  let selectableCount = 0;
  for (let col = 0; col < columns; col++) {
    if (isSelectableCell(cells, rowIndex, col)) selectableCount++;
  }
  return selectableCount;
}

function countSelectableCellsInColumn(
  cells: TableCell[][],
  colIndex: number,
  rows: number
): number {
  let selectableCount = 0;
  for (let row = 0; row < rows; row++) {
    if (isSelectableCell(cells, row, colIndex)) selectableCount++;
  }
  return selectableCount;
}

function isLineFullySelected(selectedCount: number, selectableCount: number): boolean {
  return selectableCount > 0 && selectedCount === selectableCount;
}

export function isRowFullySelected(
  cells: TableCell[][],
  selectedCells: SelectedCell[],
  rowIndex: number,
  columns: number
): boolean {
  if (selectedCells.length === 0) return false;

  let selectedCount = 0;
  for (const cell of selectedCells) {
    if (cell.row === rowIndex && isSelectableCell(cells, cell.row, cell.col)) {
      selectedCount++;
    }
  }

  return isLineFullySelected(selectedCount, countSelectableCellsInRow(cells, rowIndex, columns));
}

export function isColumnFullySelected(
  cells: TableCell[][],
  selectedCells: SelectedCell[],
  colIndex: number,
  rows: number
): boolean {
  if (selectedCells.length === 0) return false;

  let selectedCount = 0;
  for (const cell of selectedCells) {
    if (cell.col === colIndex && isSelectableCell(cells, cell.row, cell.col)) {
      selectedCount++;
    }
  }

  return isLineFullySelected(selectedCount, countSelectableCellsInColumn(cells, colIndex, rows));
}

/**
 * Derive the fully-selected rows and columns from the (small) selected-cells array without
 * scanning every row×col: bucket the selection per row/col, then only for buckets that
 * actually have a selection count that line's selectable cells. O(selectionSize × cols).
 */
export function getFullySelectedLines(
  cells: TableCell[][],
  selectedCells: SelectedCell[],
  rows: number,
  columns: number
): { fullRows: Set<number>; fullCols: Set<number> } {
  const fullRows = new Set<number>();
  const fullCols = new Set<number>();

  if (selectedCells.length === 0) {
    return { fullRows, fullCols };
  }

  const rowSelectedCounts = new Map<number, number>();
  const colSelectedCounts = new Map<number, number>();
  for (const cell of selectedCells) {
    if (!isSelectableCell(cells, cell.row, cell.col)) continue;
    rowSelectedCounts.set(cell.row, (rowSelectedCounts.get(cell.row) ?? 0) + 1);
    colSelectedCounts.set(cell.col, (colSelectedCounts.get(cell.col) ?? 0) + 1);
  }

  for (const [row, selectedCount] of rowSelectedCounts) {
    if (isLineFullySelected(selectedCount, countSelectableCellsInRow(cells, row, columns))) {
      fullRows.add(row);
    }
  }
  for (const [col, selectedCount] of colSelectedCounts) {
    if (isLineFullySelected(selectedCount, countSelectableCellsInColumn(cells, col, rows))) {
      fullCols.add(col);
    }
  }

  return { fullRows, fullCols };
}

export function isAllCellsSelected(
  cells: TableCell[][],
  selectedCells: SelectedCell[]
): boolean {
  if (selectedCells.length === 0) return false;

  const selectableCells = cells.flat().filter((c) => !c.isMerged);
  if (selectableCells.length === 0) return false;

  const selectedSet = new Set(selectedCells.map((c) => `${c.row},${c.col}`));
  return selectableCells.every((c) => selectedSet.has(`${c.rowIndex},${c.colIndex}`));
}
